import * as EventTracker from '@src/analytics/EventTracker';
import ChatGPTService from '@src/services/ChatGPTService';
import {
  useDatoClient,
  useTemplatedPrompts,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { Jodit } from 'jodit-react';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { handleCopyToClipboard } from '../../../utility/general';
import { latestSavedEntry } from '../../../utility/savedContent';
import { useFlagsCombination } from '../../../utility/useFlagsCombination';
import ContentShare from '../../common/ContentShare';
import FullScreenLoadingOverlay from '../../common/FullScreenLoadingOverlay';
import BrandVoiceRevisionModal from '../BrandVoiceRevisionModal';
import ContentActions from '../ContentActions';
import Content, { TalkingPointsSkeleton } from './Content';

import type { TalkingPointContent } from '../../../types.ts/general';

const applyH2Style = (h2: HTMLHeadingElement | null) => {
  if (h2) {
    h2.style.fontSize = '32px';
    h2.style.fontWeight = '700';
  }
};

const applyTitleStyles = (e: Element | null) => {
  if (e) {
    const title = e as HTMLElement;
    title.style.fontSize = '24px';
    title.style.fontWeight = '700';
    title.style.marginBottom = '0';
    title.style.marginTop = '0';
  }
};
const applyStoryTellerStyles = (e: Element | null) => {
  if (e) {
    const storyTeller = e as HTMLElement;
    storyTeller.style.fontSize = '16px';
    storyTeller.style.fontWeight = '400';
    storyTeller.style.lineHeight = '22.4px';
  }
};

const applyStoryInfoStyle = (e: Element | null) => {
  if (e) {
    const storyInfo = e as HTMLElement;
    storyInfo.style.marginBottom = '15px';
    storyInfo.style.gap = '5px';
  }
};

const modifyContentBlockBeforeCopy = (clonedContent: HTMLElement) => {
  const regenerateActions = clonedContent?.querySelectorAll(
    '.regenerate-action-button',
  );
  regenerateActions?.forEach(regenerateAction => {
    regenerateAction.remove();
  });

  // Apply all styles to copied content
  applyH2Style(clonedContent?.querySelector('h2'));
  applyTitleStyles(clonedContent?.querySelector('.story-title'));
  applyStoryInfoStyle(clonedContent?.querySelector('.story-info'));
  applyStoryTellerStyles(clonedContent?.querySelector('.story-teller'));

  const titles = clonedContent?.querySelectorAll('.title');
  titles?.forEach(title => {
    applyTitleStyles(title);
  });

  const strongs = clonedContent?.querySelectorAll('p strong');
  strongs?.forEach(strong => {
    (strong as HTMLElement).style.fontWeight = '800';
  });

  const blockquotes = clonedContent?.querySelectorAll('blockquote');
  blockquotes?.forEach(blockquote => {
    blockquote.style.fontStyle = 'italic';
  });

  const uls = clonedContent?.querySelectorAll('ul');
  uls?.forEach(ul => {
    ul.style.listStyleType = 'disc';
  });

  const ols = clonedContent?.querySelectorAll('ol');
  ols?.forEach(ol => {
    ol.style.listStyleType = 'none';
  });

  const lis = clonedContent?.querySelectorAll('li');
  lis?.forEach(li => {
    li.style.fontSize = '16px';
    li.style.lineHeight = '1.6';
    li.style.marginBottom = '20px';
  });

  const liOl = clonedContent?.querySelectorAll('ol > li');
  liOl?.forEach(liElement => {
    const li = liElement as HTMLElement;
    li.style.fontStyle = 'italic';
    li.style.marginBottom = '20px';
  });
};

const TalkingPointStudio = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const datoClientStore = useDatoClient();
  const templatedPromptsStore = useTemplatedPrompts();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const savedContent = videoCreator.story?.savedTalkingPointContent;
    if (!videoCreator.talkingPointContent && savedContent) {
      const latest = latestSavedEntry(savedContent);
      if (latest) {
        videoCreator.talkingPointContent = latest.entry.content;
      }
    }
  }, []);

  const content = videoCreator.talkingPointContent;
  const contentIsEmpty =
    !content || (content && !Object.values(content).length);

  const {
    enableTalkingPointsStudioSave,
    enableTalkingPointsStudioRegeneration,
  } = useFlagsCombination(userIdentity.currentRole);

  const handleSaveContent = async () => {
    if (contentIsEmpty || !contentRef.current) return;
    try {
      setIsSaving(true);

      const keys = Object.keys(
        videoCreator.talkingPointContent!,
      ) as (keyof TalkingPointContent)[];

      const content = {} as Record<
        keyof TalkingPointContent,
        Record<'title' | 'content' | 'prompt', string>
      >;

      for (let key of keys) {
        const field = contentRef.current.querySelector(`#talkingpoint-${key}`);
        if (!field) continue;
        const title = field.querySelector('.title')?.innerHTML;
        const fieldContent = field.querySelector('.content')?.innerHTML;
        let prompt = '';
        if (videoCreator.talkingPointContent) {
          prompt = videoCreator.talkingPointContent[key].prompt;
        }
        content[key] = { title: title!, content: fieldContent!, prompt };
      }

      const title = 'Fundraising Talking Points';
      const updatedTalkingPointContent =
        await datoClientStore.storyRepository?.saveTalkingPointContent(
          videoCreator.story!,
          content,
          title,
        );

      runInAction(
        () =>
          (videoCreator.story!.savedTalkingPointContent =
            updatedTalkingPointContent),
      );
    } catch (error) {
      console.log('Error occurred: ', error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (copied) {
      setTimeout(() => {
        setCopied(false);
      }, 3000);
    }
  }, [copied]);

  const handleCopy = (type: 'text' | 'html') => {
    const contentElement = contentRef?.current;
    const clonedContent = contentElement?.cloneNode(true) as HTMLElement;
    modifyContentBlockBeforeCopy(clonedContent);
    const updatedContent = clonedContent?.innerHTML || '';

    let contentToCopy: string;

    if (type === 'html') {
      contentToCopy = updatedContent;
      handleCopyToClipboard(contentToCopy, 'html');
    } else {
      contentToCopy = Jodit.modules.Helpers.stripTags(updatedContent);
      handleCopyToClipboard(contentToCopy, 'plain');
    }

    setCopied(true);

    EventTracker.trackTalkingPointsCopied({
      story_id: videoCreator.story?.id,
      story_title: videoCreator.story?.title,
    });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);

  const aiTalkingPointsTemplate = templatedPromptsStore.aiTalkingPointsTemplate;
  const gptServiceRef = useRef(
    new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
  );
  const gptService = gptServiceRef.current;

  const handleGenerate = async () => {
    setIsLoading(true);
    setIsRegenerating(false);
    EventTracker.trackTalkingPointsProducerOpened({
      story_id: videoCreator.story?.id,
      story_title: videoCreator.story?.title,
      action_source: 'creator_studio',
    });
    EventTracker.trackTalkingPointsGenerationStarted({
      story_id: videoCreator.story?.id,
      story_title: videoCreator.story?.title,
      template: aiTalkingPointsTemplate?.title,
    });
    await gptService.generateTalkingPoint({
      setLoading: setIsLoading,
      template: aiTalkingPointsTemplate,
      avoidWords: videoCreator.organization?.branding?.avoidWords,
    });
  };

  const [revisionModalOpen, setRevisionModalOpen] = useState(false);
  const handleRegenerate = async (revision: string) => {
    setIsLoading(true);
    setIsRegenerating(true);
    setRevisionModalOpen(false);
    EventTracker.trackTalkingPointsRegenerationStarted({
      story_id: videoCreator.story?.id,
      story_title: videoCreator.story?.title,
      template: aiTalkingPointsTemplate?.title,
      revision,
    });
    await gptService.generateTalkingPoint({
      setLoading: setIsLoading,
      template: aiTalkingPointsTemplate,
      brandText: revision,
      avoidWords: videoCreator.organization?.branding?.avoidWords,
    });
  };

  return (
    <Main>
      {isSaving && (
        <FullScreenLoadingOverlay text="Saving talking point content…" />
      )}

      <ContentActions
        hasBeenGenerated={!contentIsEmpty}
        onGenerate={handleGenerate}
        onRegenerate={
          enableTalkingPointsStudioRegeneration
            ? () => setRevisionModalOpen(true)
            : undefined
        }
        renderShare={
          !contentIsEmpty
            ? () => (
                <ContentShare
                  type="Blog"
                  onCopy={() => handleCopy('html')}
                  onCopyText={() => handleCopy('text')}
                />
              )
            : undefined
        }
        onSave={
          !contentIsEmpty && enableTalkingPointsStudioSave
            ? handleSaveContent
            : undefined
        }
      />

      {revisionModalOpen ? (
        <BrandVoiceRevisionModal
          title="Regenerate Talking Points"
          onClose={() => setRevisionModalOpen(false)}
          handleGenerate={handleRegenerate}
        />
      ) : null}

      {isLoading && contentIsEmpty && (
        <TalkingPointsSkeleton
          label={`${
            isRegenerating ? 'Regenerating' : 'Generating'
          } talking points…`}
        />
      )}
      <Content ref={contentRef} />
    </Main>
  );
});

export default TalkingPointStudio;

const Main = styled.div`
  display: flex;
  flex-direction: column;
`;
